/**
 * Parent Portal Service
 *
 * Handles API calls for parent-specific functionality including:
 * - Viewing assigned children (students)
 * - Accessing consent forms
 * - Submitting consent forms for children
 */

import apiClient from './apiClient';

/**
 * Get list of children (patients) assigned to the current parent
 * Uses staff assignment system - parents are assigned to students via assigned_to_me
 *
 * @returns {Promise<{children: Array, total: number}>} List of assigned children
 */
export const getMyChildren = async () => {
  try {
    const response = await apiClient.get('/parent/children');
    return response.data;
  } catch (error) {
    console.error('Error fetching children:', error);
    throw error;
  }
};

/**
 * Get list of active consent forms
 * Returns all published consent forms (category='consent') that parents can fill
 *
 * @returns {Promise<{forms: Array, total: number}>} List of available consent forms
 */
export const getConsentForms = async () => {
  try {
    const response = await apiClient.get('/parent/consent-forms');
    return response.data;
  } catch (error) {
    console.error('Error fetching consent forms:', error);
    throw error;
  }
};

/**
 * Get a specific consent form by ID with full structure (fields)
 * Parents need this to fill out consent forms for their assigned children
 *
 * @param {string} formId - ID of the consent form to retrieve
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Form data with fields
 */
export const getConsentFormById = async (formId) => {
  try {
    const response = await apiClient.get(`/parent/consent-forms/${formId}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error fetching consent form:', error);
    return { success: false, message: error.response?.data?.detail || error.message };
  }
};

/**
 * Submit a consent form for a child
 *
 * Verifies:
 * 1. Parent is assigned to the student (via staff assignment)
 * 2. Form exists and is a consent form
 * 3. Submits the form on behalf of the student
 *
 * @param {string} formId - ID of the consent form to submit
 * @param {string} studentPatientId - ID of the student (patient) the consent is for
 * @param {Object} formData - Form field data
 * @returns {Promise<{success: boolean, submission_id: string, message: string}>}
 */
export const submitConsentForm = async (formId, studentPatientId, formData) => {
  try {
    const response = await apiClient.post(
      `/parent/consent-forms/${formId}/submit`,
      formData,
      {
        params: {
          student_patient_id: studentPatientId
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error submitting consent form:', error);
    throw error;
  }
};

/**
 * Service object with all parent-related API methods
 */
const parentService = {
  getMyChildren,
  getConsentForms,
  getConsentFormById,
  submitConsentForm
};

export default parentService;
